import React, { createContext, useContext, useEffect, useState } from "react";
import api from "@/lib/api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    const raw = localStorage.getItem("csc_user");
    return raw ? JSON.parse(raw) : null;
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("csc_token");
    if (!token) { setLoading(false); return; }
    api.get("/auth/me")
      .then((r) => { setUser(r.data); localStorage.setItem("csc_user", JSON.stringify(r.data)); })
      .catch(() => { localStorage.removeItem("csc_token"); localStorage.removeItem("csc_user"); setUser(null); })
      .finally(() => setLoading(false));
  }, []);

  const login = async (email, password, options = {}) => {
    const { data } = await api.post("/auth/login", { email, password, branch: options.branch || "", remember: !!options.remember });
    localStorage.setItem("csc_token", data.token);
    localStorage.setItem("csc_user", JSON.stringify(data.user));
    setUser(data.user);
    return data.user;
  };

  const pinLogin = async (email, pin, deviceId) => {
    const { data } = await api.post("/auth/pin/login", { email, pin, device_id: deviceId });
    localStorage.setItem("csc_token", data.token);
    localStorage.setItem("csc_user", JSON.stringify(data.user));
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); }
    catch (err) { console.warn("logout endpoint failed (clearing local session anyway):", err?.message || err); }
    localStorage.removeItem("csc_token");
    localStorage.removeItem("csc_user");
    setUser(null);
  };

  // Idle timeout — 30 min of no user activity auto-logs out (session security).
  useEffect(() => {
    if (!user) return;
    const IDLE_MS = 30 * 60 * 1000;
    let timer = null;
    const reset = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        console.warn("Auto-logout: session idle for 30 minutes");
        logout();
      }, IDLE_MS);
    };
    const events = ["mousedown", "keydown", "touchstart", "scroll"];
    events.forEach((ev) => window.addEventListener(ev, reset, { passive: true }));
    reset();
    return () => {
      if (timer) clearTimeout(timer);
      events.forEach((ev) => window.removeEventListener(ev, reset));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  return (
    <AuthCtx.Provider value={{ user, loading, login, pinLogin, logout }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
